/**
 * Done. / ChiroCPD — Shared Utilities
 *
 * Pure functions with no side effects. No dependencies beyond standard library.
 */

import type { HealthStatus, CapStatus } from '@/lib/types';

// ── Date formatting ───────────────────────────────────────────────────────────

/**
 * Format an ISO date string (YYYY-MM-DD) to NZ display format (DD MMM YYYY).
 * e.g. "2026-03-28" → "28 Mar 2026"
 */
export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format a Date object to YYYY-MM-DD for form inputs and API payloads.
 */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${String(y)}-${m}-${d}`;
}

/**
 * Returns today's date as YYYY-MM-DD.
 */
export function todayIso(): string {
  return toIsoDate(new Date());
}

// ── Hours formatting ──────────────────────────────────────────────────────────

/**
 * Format hours to 1 decimal place, dropping the trailing zero when whole.
 * e.g. 4.0 → "4", 4.5 → "4.5"
 */
export function formatHours(hours: number): string {
  return hours % 1 === 0 ? String(hours) : hours.toFixed(1);
}

/**
 * Returns the user-facing hours remaining label.
 * e.g. "14.5 hrs remaining this year"
 */
export function hoursRemainingLabel(logged: number, cap: number): string {
  const remaining = Math.max(0, cap - logged);
  return `${formatHours(remaining)} hrs remaining this year`;
}

// ── Cap health ────────────────────────────────────────────────────────────────

/**
 * Derive health status from a ratio of used/max.
 * warning: ≥ 80% used | exhausted: 100% used | healthy: < 80%
 */
export function capHealth(used: number, max: number): HealthStatus {
  if (max === 0) return 'exhausted';
  const ratio = used / max;
  if (ratio >= 1) return 'exhausted';
  if (ratio >= 0.8) return 'warning';
  return 'healthy';
}

/**
 * Derive health status from a CapStatus object for hours.
 */
export function hourCapHealth(cap: CapStatus): HealthStatus {
  return capHealth(cap.annualHoursLogged, cap.annualHourCap);
}

/**
 * Derive health status from a CapStatus object for AI generations.
 */
export function aiCapHealth(cap: CapStatus): HealthStatus {
  return capHealth(cap.aiGenerationsUsed, cap.aiGenerationCap);
}

// ── Subscription ──────────────────────────────────────────────────────────────

/**
 * Returns true if the subscription allows full access.
 */
export function isSubscriptionActive(status: string, expiresAt?: string | null): boolean {
  if (status === 'active') return true;
  if (status === 'past_due' || status === 'cancelled') {
    if (!expiresAt) return false;
    const graceEnd = new Date(expiresAt).getTime() + 7 * 86_400_000;
    return Date.now() < graceEnd;
  }
  return false;
}

// ── User initials ─────────────────────────────────────────────────────────────

/**
 * Extract 1–2 character initials from a name.
 * e.g. "Jo Miles" → "JM" | "Jo" → "J"
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0).toUpperCase() ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0).toUpperCase() ?? '') : '';
  return first + last;
}

// ── Class name helper ─────────────────────────────────────────────────────────

/**
 * Lightweight cx() — filters falsy values and joins class names.
 * Avoids adding clsx as a dependency.
 */
export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Pluralise ────────────────────────────────────────────────────────────────

export function pluralise(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

// ── YouTube thumbnail ─────────────────────────────────────────────────────────

export function getYouTubeThumbnail(videoUrl: string): string | null {
  const match = videoUrl.match(/youtube\.com\/embed\/([^/?]+)/);
  if (!match) return null;
  return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
}

// ── Current CPD year ──────────────────────────────────────────────────────────

/**
 * The CPD year aligns with the calendar year.
 * Annual Plan due 1 March; Annual Review due 31 December.
 */
export function currentCpdYear(): number {
  return new Date().getFullYear();
}
